import { QuestionType2UIConfig } from "../../../../../components/ui/question-type2-ui";
import { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from "../../../../../cvp-webgames-core/Components/ui/react/core-general-question-ui";
import BaseScenarioPart from "../../../../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { VideoSegmentItem, SingleVideoSegmentItem } from "../../../../../cvp-webgames-core/VideoSegment/VideoSegmentItem";
import { ScoringType } from "../../../../../misc/tools";
import { GameBaseScenario } from "../../../../GameBaseScenario";
import Scenario4WcWorkStoppage, { scenario4WcWorkStoppageAudioCaching, scenario4WcWorkStoppageVideoSegments } from "../../../scenario4-wc-work-stoppage";
import { WcWorkStoppageComponentsStep1ComponentMainConfig, WcWorkStoppageComponentsStep1ComponentPart1Config } from "../wc-work-stoppage-components-step1";

export default class WcWorkStoppageComponentsStep1Part1 extends BaseScenarioPart<GameBaseScenario> {
	private stepName = 'Step 2'
	private get config(): WcWorkStoppageComponentsStep1ComponentPart1Config {
		return this.mainConfig.part1Config;
	}
	constructor(scenario: GameBaseScenario, private onDone: () => void, private id: string, private mainConfig: WcWorkStoppageComponentsStep1ComponentMainConfig) {
		super(scenario);
	}

	public get partId(): string {
		return this.id;
	}

	public get videoUrl(): string[] {
		return [this.config.videoUrl];
	}

	public start() {
		this.section1();
	}

	public section1() {
		const context = this;
        this.mainConfig.scenario.setAreaForCurrentScenario(6);
		this.mainConfig.scenario.uiWrapper.displayMixedMediaUI({
			title: this.mainConfig.scenario.props.name,
			subtitle: this.stepName,
			htmlData: `
				<p class="default-text add-pad">For the substitution test, you approach other employees and inquire as to whether they would do the same 
				thing in similar circumstances.<br/><br/>
				Physician #1: There is no question that the clinic is understaffed, and 
				we are pressured to see many patients in a short period of time. It is very frustrating. Supplies 
				are often not easily found, but I can also tell you that no matter how pressured or frustrated I 
				was, I would not violate appropriate aseptic technique.<br/><br/>
				Physician #2: I would have taken my gloves off, found the supplies, washed 
				my hands and re-gloved. Not doing so puts the patient at risk.<br/><br/>
				Physician #3: Nursing staff are often not available to help as they are 
				often in-processing patients. We are double booked often, but I would not contaminate my hands then touch an open wound. Period.</p>
			`,
			okButton: 'Continue',
			talkingVideoSrc: scenario4WcWorkStoppageVideoSegments.getItemByKey('MixedMedia').src,
			talkingVideoChapterId: scenario4WcWorkStoppageVideoSegments.getItemByKey('MixedMedia').chapterIds[0],
			onContinueClicked: () => {
				context.section2();
			}
		})
	}

	public section2() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType2UI({
			title: this.mainConfig.scenario.props.name,
			audioUrl: scenario4WcWorkStoppageAudioCaching.getItemByKey(scenario4WcWorkStoppageAudioCaching.audioKeys.question3).src,
			subtitle: (this.stepName + ' - Question 1'),
			isYesCorrect: false,
			correctFeedback: `
				<p class="default-text">Correct. None of the other staff physicians identified any 
				circumstance where they would act as Dr. Jones did. While there are systems issues related 
				to staffing and a culture that is highly focused on productivity, the substitution test 
				is not satisfied, accountability lies with the employee.</p>
			`,
			wrongFeedback: `
				<p class="default-text">Not the best option. While other staff physicians acknowledged long 
				standing issues in the clinic with staffing and workflow, none identified any circumstance 
				where they would act as Dr. Jones did.</p>
			`,
			question: 'Given the information received from staff interviews, is it reasonable to conclude that a systems or cultural issue exists, and other employees in that work area would behave the same way in a similar situation?',
			onSubmitClicked: (isCorrect: boolean, feedback: string, selectedTitle: string, parentQuestion: QuestionType2UIConfig) => {
				const mentorInfo = scenario4WcWorkStoppageVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType2(isCorrect, feedback, selectedTitle, (this.stepName + ' - Question 1 '), parentQuestion, () => {
					context.section3();
				}, () => {
					context.section3();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[6], mentorInfo.chapterIds[7]);
			}
		}, 2);
	}

	public section3() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType1UI({
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 2'),
			audioUrl: scenario4WcWorkStoppageAudioCaching.getItemByKey(scenario4WcWorkStoppageAudioCaching.audioKeys.question4).src,
			questionTitle: 'Based on the substitution test, the accountability for this action…',
			options: [
				{
					title: 'Lies completely with the employee.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Correct. The substitution test is NOT satisfied. 
						Therefore, accountability for this action rests mostly with the employee.</p>
					`,
					isCorrect: true,
					score: ScoringType.good
				},
				{
					title: 'Is shared between the employee and organizational leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. The accountability 
						for this action lies completely with the employee. This event did not 
						happen because of the existing culture and systematic deficiencies in 
						knowledge within the organization.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Lies solely with the leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. The substitution 
						test demonstrates that there are no system or cultural issues identified. 
						Accountability for the employee’s choice rests completely with them.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				}
			],
			
			onSubmitClicked: (allOptions: GeneralQuestionUIConfigOption[], selectedChoices: GeneralQuestionUIConfigOption[], parentQuestionConfig: GeneralQuestionUIConfig) => {
				const mentorInfo = scenario4WcWorkStoppageVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType1(selectedChoices[0], (this.stepName + ' - Question 2 '), parentQuestionConfig, () => {
					this.onDone();
				}, () => {
					this.onDone();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[8], mentorInfo.chapterIds[9], mentorInfo.chapterIds[10], 'Is shared between the employee and organizational leadership.');
			}
		}, 3);
	}
}